import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import types from '@/pages/Device/DeviceType'
import jobStates, { erroneousJobStates, Installed } from '@/pages/fota/jobState'
import { FilterConfig } from '@/models/filters'

// Base device filters - used by most device-related pages
export const useDeviceFilters = (): FilterConfig => {
  const { t } = useTranslation(['di', 'app'])

  return useMemo(
    () => ({
      groups: [
        {
          id: 'type',
          label: t('di:device.type'),
          options: Object.entries(types).map(([key, label]) => ({
            key,
            label,
            value: key,
          })),
        },
      ],
    }),
    [t],
  )
}

// Extended filters for device-Type
export const useDeviceTypeFilters = (): FilterConfig => {
  const baseFilters = useDeviceFilters()
  return useMemo(
    () => ({
      groups: [...baseFilters.groups],
    }),
    [baseFilters],
  )
}

export const useFotaFailedFilters = (): FilterConfig => {
  const { t } = useTranslation(['di', 'app'])

  return useMemo(
    () => ({
      groups: [
        {
          id: 'failedfotaState',
          label: t('di:fota.state'),
          options: Object.entries(erroneousJobStates).map(([key, label]) => ({
            key,
            label,
            value: key,
          })),
        },
      ],
    }),
    [t],
  )
}

// FOTA Installed filters
export const useFotaInstalledFilters = (): FilterConfig => {
  const { t } = useTranslation(['di', 'app'])

  return useMemo(
    () => ({
      groups: [
        {
          id: 'installedfotaState',
          label: t('app:device.fotaJobs.installed'),
          options: [
            {
              key: Installed,
              label: t('app:device.fotaJobs.installed'),
              value: Installed,
            },
          ],
        },
      ],
    }),
    [t],
  )
}

// FOTA All filters
export const useFotaAllFilters = (): FilterConfig => {
  const { t } = useTranslation(['di', 'app'])

  return useMemo(
    () => ({
      groups: [
        {
          id: 'allfotaState',
          label: t('app:device.fotaJobs.state'),
          options: Object.entries(jobStates).map(([key, label]) => ({
            key,
            label,
            value: key,
          })),
        },
      ],
    }),
    [t],
  )
}

export const isOnlineBooleanOptions: Record<string, string> = {
  ['true']: 'ONLINE',
  ['false']: 'NOT ONLINE',
}

export const inServiceBooleanOptions: Record<string, string> = {
  ['true']: 'IN SERVICE',
  ['false']: 'NOT IN SERVICE',
}

export const useFotaCreateFilters = (): FilterConfig => {
  const baseFilters = useDeviceFilters()
  const { t } = useTranslation(['di', 'app'])

  return useMemo(
    () => ({
      groups: [
        ...baseFilters.groups,
        {
          id: 'is_online',
          label: t('di:device.isOnlineFilter'),
          options: Object.entries(isOnlineBooleanOptions).map(
            ([key, label]) => ({
              key,
              label: label,
              value: key,
            }),
          ),
        },
        {
          id: 'in_service',
          label: t('di:device.inServiceFilter'),
          options: Object.entries(inServiceBooleanOptions).map(
            ([key, label]) => ({
              key,
              label: label,
              value: key,
            }),
          ),
        },
      ],
    }),
    [baseFilters, t],
  )
}
